import { useCallback, useEffect, useState } from "react";
import { api, ApiError, blobProtegido, type MeusDados as Dados, type Pedidos } from "../api";
import FotoTresPorQuatro from "./FotoTresPorQuatro";
import ContaBancariaForm from "./ContaBancariaForm";
import StatusPedido from "./StatusPedido";

/**
 * MEUS DADOS — o cadastro que o RH/DP tem da pessoa, e o que vem com ele.
 *
 * O cadastro tem UMA fonte (RH/DP). O colaborador não edita direto: ele PEDE
 * — conta bancária nova e foto 3x4 nova — e o DP aprova. Só então o cadastro
 * muda, e a tela mostra em que pé está cada pedido.
 *
 * Os dados bancários ficam à mostra (a pessoa precisa conferir onde o salário
 * cai). Só o SALÁRIO fica escondido até ela pedir: a tela é aberta no celular,
 * no meio da obra, com colega do lado.
 */

type Valor = string | number | boolean | null | undefined;

function texto(v: Valor): string {
  if (v == null || v === "") return "—";
  return String(v);
}

function reais(v: Valor): string {
  const n = Number(v);
  if (v == null || v === "" || !Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function cpf(v: Valor): string {
  const d = String(v ?? "").replace(/\D/g, "");
  if (d.length !== 11) return texto(v);
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="dado">
      <span className="dado-rotulo">{rotulo}</span>
      <span className="dado-valor">{valor}</span>
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="grupo">
      <header className="grupo-topo">
        <span className="grupo-titulo">{titulo}</span>
      </header>
      {children}
    </section>
  );
}

export default function MeusDados() {
  const [dados, setDados] = useState<Dados | null>(null);
  const [pedidos, setPedidos] = useState<Pedidos | null>(null);
  const [foto, setFoto] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [mostrarSalario, setMostrarSalario] = useState(false);
  const [editando, setEditando] = useState<"foto" | "conta" | null>(null);

  const carregar = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([api.meusDados(), api.pedidos()]);
      setDados(d);
      setPedidos(p);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar seus dados.");
    }
    blobProtegido("/api/minha-foto")
      .then((u) =>
        setFoto((antiga) => {
          if (antiga) URL.revokeObjectURL(antiga);
          return u;
        }),
      )
      .catch(() => setFoto(null));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (erro) return <div className="aviso-erro">{erro}</div>;
  if (!dados) return <div className="carregando">Carregando…</div>;

  const c = dados.cadastro;
  const oculto = "••••••";
  // O pedido mais recente de cada tipo (a lista vem do mais novo para o mais antigo).
  const ultimo = (tipo: string) => pedidos?.solicitacoes.find((p) => p.tipo === tipo) ?? null;
  const pedidoFoto = ultimo("foto");
  const pedidoConta = ultimo("conta_bancaria");

  function concluido(mensagem: string) {
    setEditando(null);
    setAviso(mensagem);
    carregar();
    window.scrollTo(0, 0);
  }

  function editar(qual: "foto" | "conta") {
    setAviso(null);
    setEditando(qual);
  }

  return (
    <div className="pagina">
      {aviso && <div className="aviso-ok">{aviso}</div>}

      <Bloco titulo="Foto 3x4">
        {editando === "foto" ? (
          <FotoTresPorQuatro onEnviada={concluido} onCancelar={() => setEditando(null)} />
        ) : (
          <div className="foto34-atual">
            {foto ? (
              <img className="foto34-img" src={foto} alt="Sua foto no cadastro" />
            ) : (
              <div className="foto34-img foto34-sem">Sem foto</div>
            )}
            <div className="foto34-lado">
              <button className="botao-secundario" onClick={() => editar("foto")}>
                Enviar nova foto 3x4
              </button>
              {pedidoFoto && <StatusPedido pedido={pedidoFoto} onMudou={carregar} />}
            </div>
          </div>
        )}
      </Bloco>

      <Bloco titulo="Contrato">
        <div className="dados">
          <Campo rotulo="Cargo" valor={texto(c.cargo)} />
          <Campo rotulo="Departamento" valor={texto(c.departamento)} />
          <Campo rotulo="Empresa" valor={texto(c.empresa)} />
          <Campo rotulo="Contrato" valor={texto(c.contrato)} />
          <Campo rotulo="Matrícula" valor={texto(c.matricula)} />
          <Campo rotulo="Admissão" valor={texto(c.data_admissao)} />
          <Campo rotulo="Salário" valor={mostrarSalario ? reais(c.salario) : oculto} />
        </div>
        <button className="botao-texto botao-alinhado" onClick={() => setMostrarSalario((v) => !v)}>
          {mostrarSalario ? "Ocultar salário" : "Mostrar salário"}
        </button>
      </Bloco>

      <Bloco titulo="Dados pessoais">
        <div className="dados">
          <Campo rotulo="Nome" valor={texto(c.nome)} />
          <Campo rotulo="CPF" valor={cpf(c.cpf)} />
          <Campo rotulo="Nascimento" valor={texto(c.data_nascimento)} />
          <Campo rotulo="RG" valor={texto(c.rg)} />
          <Campo rotulo="PIS" valor={texto(c.pis)} />
          <Campo
            rotulo="CTPS"
            valor={c.ctps ? `${c.ctps}${c.ctps_serie ? ` / série ${c.ctps_serie}` : ""}` : "—"}
          />
          <Campo rotulo="Estado civil" valor={texto(c.estado_civil)} />
          <Campo rotulo="Mãe" valor={texto(c.nome_mae)} />
          <Campo rotulo="Pai" valor={texto(c.nome_pai)} />
        </div>
      </Bloco>

      <Bloco titulo="Contato">
        <div className="dados">
          <Campo rotulo="E-mail" valor={texto(c.email)} />
          <Campo rotulo="Telefone" valor={texto(c.telefone)} />
          <Campo rotulo="Endereço" valor={texto(c.endereco)} />
          <Campo rotulo="Cidade" valor={texto(c.cidade)} />
          <Campo rotulo="CEP" valor={texto(c.cep)} />
          <Campo
            rotulo="Emergência"
            valor={
              c.contato_emergencia
                ? `${c.contato_emergencia}${c.parente_emergencia ? ` (${c.parente_emergencia})` : ""}`
                : "—"
            }
          />
        </div>
      </Bloco>

      <Bloco titulo="Dados bancários">
        {editando === "conta" ? (
          <ContaBancariaForm
            tiposConta={pedidos?.tiposConta ?? ["Corrente", "Poupança", "Salário"]}
            onEnviado={concluido}
            onCancelar={() => setEditando(null)}
          />
        ) : (
          <>
            <div className="dados">
              <Campo rotulo="Banco" valor={texto(c.banco)} />
              <Campo rotulo="Tipo de conta" valor={texto(c.tipo_conta)} />
              <Campo rotulo="Agência" valor={texto(c.agencia)} />
              <Campo rotulo="Conta" valor={texto(c.conta)} />
              <Campo rotulo="PIX" valor={texto(c.pix)} />
            </div>
            {pedidoConta && (
              <StatusPedido
                pedido={pedidoConta}
                descricao={
                  pedidoConta.dados
                    ? `${pedidoConta.dados.banco ?? ""} · ag. ${pedidoConta.dados.agencia ?? ""} · conta ${pedidoConta.dados.conta ?? ""}`
                    : undefined
                }
                onMudou={carregar}
              />
            )}
            <button className="botao-secundario" onClick={() => editar("conta")}>
              Cadastrar nova conta bancária
            </button>
          </>
        )}
      </Bloco>

      <Bloco titulo="Benefícios">
        {dados.beneficios.length === 0 ? (
          <p className="vazio-curto">Nenhum benefício cadastrado.</p>
        ) : (
          <ul className="lista">
            {dados.beneficios.map((b, i) => (
              <li key={i} className="item">
                <div className="item-linha">
                  <span className="item-principal">{texto(b.tipo)}</span>
                  <strong className="item-valor">{b.valor != null ? reais(b.valor) : ""}</strong>
                </div>
                <div className="item-linha item-detalhe">
                  <span>{b.data_inicio ? `Desde ${b.data_inicio}` : ""}</span>
                  <span>{texto(b.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco titulo="Férias">
        {dados.ferias.length === 0 ? (
          <p className="vazio-curto">Nenhuma férias registrada.</p>
        ) : (
          <ul className="lista">
            {dados.ferias.map((f, i) => (
              <li key={i} className="item">
                <div className="item-linha">
                  <span className="item-principal">
                    {f.data_inicio ? `${f.data_inicio} a ${texto(f.data_fim)}` : "Período a definir"}
                  </span>
                  <strong className="item-valor">{f.dias ? `${f.dias} dias` : ""}</strong>
                </div>
                <div className="item-linha item-detalhe">
                  <span>
                    {f.aquisitivo_inicio
                      ? `Aquisitivo ${f.aquisitivo_inicio} – ${texto(f.aquisitivo_fim)}`
                      : ""}
                  </span>
                  <span>{texto(f.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco titulo="Documentos com vencimento">
        {dados.controlados.length === 0 ? (
          <p className="vazio-curto">Nenhum documento com vencimento cadastrado.</p>
        ) : (
          <ul className="lista">
            {dados.controlados.map((d, i) => {
              const dias = d.dias_para_vencer == null ? null : Number(d.dias_para_vencer);
              const situacao =
                dias == null
                  ? ""
                  : dias < 0
                    ? "Vencido"
                    : dias === 0
                      ? "Vence hoje"
                      : dias <= 30
                        ? `Vence em ${dias} dia${dias === 1 ? "" : "s"}`
                        : "Em dia";
              return (
                <li key={i} className="item">
                  <div className="item-linha">
                    <span className="item-principal">{texto(d.tipo_documento)}</span>
                    <span className={dias != null && dias < 0 ? "negativo" : "item-valor"}>
                      {situacao}
                    </span>
                  </div>
                  <div className="item-linha item-detalhe">
                    <span>{d.numero ? `Nº ${d.numero}` : ""}</span>
                    <span>{d.data_vencimento ? `Validade ${d.data_vencimento}` : ""}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Bloco>

      <Bloco titulo="Dependentes">
        {dados.dependentes.length === 0 ? (
          <p className="vazio-curto">Nenhum dependente cadastrado.</p>
        ) : (
          <ul className="lista">
            {dados.dependentes.map((d, i) => (
              <li key={i} className="item">
                <div className="item-linha">
                  <span className="item-principal">{texto(d.nome)}</span>
                  <span className="item-valor">{texto(d.parentesco)}</span>
                </div>
                {d.data_nascimento && (
                  <div className="item-linha item-detalhe">
                    <span>Nascimento {d.data_nascimento}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco titulo="Treinamentos">
        {dados.treinamentos.length === 0 ? (
          <p className="vazio-curto">Nenhum treinamento registrado.</p>
        ) : (
          <ul className="lista">
            {dados.treinamentos.map((t, i) => (
              <li key={i} className="item">
                <div className="item-linha">
                  <span className="item-principal">{texto(t.nome_treinamento)}</span>
                  <span className="item-valor">{t.carga_horaria ? `${t.carga_horaria}h` : ""}</span>
                </div>
                <div className="item-linha item-detalhe">
                  <span>{texto(t.data)}</span>
                  <span>{texto(t.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <p className="explica">
        Algum outro dado errado ou desatualizado? Procure o <strong>Departamento Pessoal</strong> — a
        correção é feita no cadastro oficial e aparece aqui em seguida.
      </p>
    </div>
  );
}
